/**
 * movePoolAdditions.ts — moves Pokémon GO has granted that the shipped Game
 * Master has not caught up with yet (2026-09-16).
 *
 * WHY THIS EXISTS. Every move pool in the app comes from the PokéMiners Game
 * Master dump (raidMeta.generated.ts, refreshed weekly). That dump lags the
 * live game: the GO Battle League Twilight Trails season landed on 2026-09-08
 * with three dozen new species/move pairings, and PokéMiners' latest.json
 * still carried the 2026-08-29 state a week later. In that window a trainer's
 * own screenshot of a Houndoom with Incinerate / Trailblaze was REJECTED by
 * the "add from screenshots" gate ("can't learn ... looks like a different
 * Pokémon's moves"), and the species page offered a pool the Pokémon in the
 * bag was not in. The game's own screen is the ground truth; this file is how
 * the app agrees with it until the generator does.
 *
 * WHAT GOES HERE. Only pairings confirmed by Niantic's own post (or the
 * trainer's own screen), dated, with the move ids the Game Master already
 * uses so the stats join needs no new numbers. Once the weekly refresh
 * carries a pairing, the entry here is redundant and harmless (every reader
 * unions), so prune on the next review rather than in a hurry.
 *
 * MEGA-ONLY "+" MOVES. Mega Squads (2026-09) introduced Charged Attacks that
 * exist only while Mega Evolved and are printed with a trailing "+" in GO
 * ("Dark Pulse+" on Mega Houndoom, an automatic third Charged Attack that
 * strengthens with Mega Level). They have no Game Master row anyone has
 * mined, so they carry their own type here and are known to the scanner and
 * the legality gate by NAME; they are not offered in the PvP picker, whose
 * numbers would be invented.
 *
^ * Sources: Niantic's GO Battle League Twilight Trails post (attack
 * availability list) and the owner's Mega Houndoom screenshot (2026-09-16).
 */

export interface MovePoolAddition {
  /** National dex number. Applies to every form of the species. */
  speciesId: number;
  /** Game Master move ids (the raidMeta / pvpMoves `id`), fast and charged. */
  fast?: readonly string[];
  charged?: readonly string[];
  /** ISO date the game granted the pairing. */
  since: string;
}

/** Twilight Trails (GBL season, 2026-09-08). */
const TWILIGHT_TRAILS = "2026-09-08";

export const MOVE_POOL_ADDITIONS: readonly MovePoolAddition[] = [
  { speciesId: 313, fast: ["INFESTATION_FAST"], charged: ["LUNGE"], since: TWILIGHT_TRAILS }, // Volbeat
  { speciesId: 314, fast: ["INFESTATION_FAST"], charged: ["SHADOW_BALL"], since: TWILIGHT_TRAILS }, // Illumise
  { speciesId: 71, fast: ["SUCKER_PUNCH_FAST"], since: TWILIGHT_TRAILS }, // Victreebel
  { speciesId: 491, fast: ["SUCKER_PUNCH_FAST"], charged: ["FOUL_PLAY"], since: TWILIGHT_TRAILS }, // Darkrai
  { speciesId: 531, fast: ["CHARGE_BEAM_FAST"], since: TWILIGHT_TRAILS }, // Audino
  { speciesId: 229, fast: ["INCINERATE_FAST"], charged: ["TRAILBLAZE"], since: TWILIGHT_TRAILS }, // Houndoom
  { speciesId: 169, fast: ["GUST_FAST"], since: TWILIGHT_TRAILS }, // Crobat
  { speciesId: 973, fast: ["PECK_FAST"], since: TWILIGHT_TRAILS }, // Flamigo
  { speciesId: 609, fast: ["ASTONISH_FAST"], since: TWILIGHT_TRAILS }, // Chandelure
  { speciesId: 386, fast: ["LOW_KICK_FAST"], since: TWILIGHT_TRAILS }, // Deoxys (Defense Forme)
  { speciesId: 983, fast: ["LOW_KICK_FAST"], since: TWILIGHT_TRAILS }, // Kingambit
  { speciesId: 901, fast: ["SCRATCH_FAST"], since: TWILIGHT_TRAILS }, // Ursaluna
  { speciesId: 945, fast: ["SCRATCH_FAST"], charged: ["FOUL_PLAY"], since: TWILIGHT_TRAILS }, // Grafaiai
  { speciesId: 143, fast: ["PSYWAVE_FAST"], since: TWILIGHT_TRAILS }, // Snorlax
  { speciesId: 24, charged: ["BRUTAL_SWING", "WRAP"], since: TWILIGHT_TRAILS }, // Arbok
  { speciesId: 142, charged: ["BRUTAL_SWING"], since: TWILIGHT_TRAILS }, // Aerodactyl
  { speciesId: 89, charged: ["BRUTAL_SWING", "ICE_PUNCH"], since: TWILIGHT_TRAILS }, // Alolan Muk
  { speciesId: 658, charged: ["BRUTAL_SWING"], since: TWILIGHT_TRAILS }, // Greninja
  { speciesId: 168, charged: ["FOUL_PLAY"], since: TWILIGHT_TRAILS }, // Ariados
  { speciesId: 26, charged: ["VOLT_TACKLE"], since: TWILIGHT_TRAILS }, // Raichu + Alolan Raichu
  { speciesId: 861, charged: ["DRAINING_KISS"], since: TWILIGHT_TRAILS }, // Grimmsnarl
  { speciesId: 306, charged: ["BRICK_BREAK"], since: TWILIGHT_TRAILS }, // Aggron
  { speciesId: 807, charged: ["DYNAMIC_PUNCH"], since: TWILIGHT_TRAILS }, // Zeraora
  { speciesId: 475, charged: ["SACRED_SWORD"], since: TWILIGHT_TRAILS }, // Gallade
  { speciesId: 429, charged: ["MYSTICAL_FIRE"], since: TWILIGHT_TRAILS }, // Mismagius
  { speciesId: 563, charged: ["ENERGY_BALL"], since: TWILIGHT_TRAILS }, // Cofagrigus
  { speciesId: 227, charged: ["DRILL_RUN"], since: TWILIGHT_TRAILS }, // Skarmory
  { speciesId: 962, charged: ["DRILL_RUN"], since: TWILIGHT_TRAILS }, // Bombirdier
  { speciesId: 249, charged: ["EARTH_POWER"], since: TWILIGHT_TRAILS }, // Lugia
  { speciesId: 241, charged: ["HIGH_HORSEPOWER"], since: TWILIGHT_TRAILS }, // Miltank
  { speciesId: 34, charged: ["AVALANCHE"], since: TWILIGHT_TRAILS }, // Nidoking
  { speciesId: 571, charged: ["SWIFT"], since: TWILIGHT_TRAILS }, // Hisuian Zoroark
  { speciesId: 849, charged: ["SWIFT"], since: TWILIGHT_TRAILS }, // Toxtricity (both forms)
];

export interface MegaOnlyMove {
  speciesId: number;
  /** Exactly as GO prints it, "+" included. */
  name: string;
  /** Lowercase GO type key. */
  type: string;
  since: string;
}

/** Charged Attacks that exist only while Mega Evolved (printed with a "+"). */
export const MEGA_ONLY_MOVES: readonly MegaOnlyMove[] = [
  { speciesId: 229, name: "Dark Pulse+", type: "dark", since: "2026-09-08" }, // Mega Houndoom
];

/** Move ids the additions grant a species, fast and charged apart. */
export function movePoolAdditionsFor(speciesId: number): { fast: string[]; charged: string[] } {
  const fast: string[] = [];
  const charged: string[] = [];
  for (const a of MOVE_POOL_ADDITIONS) {
    if (a.speciesId !== speciesId) continue;
    for (const id of a.fast ?? []) if (!fast.includes(id)) fast.push(id);
    for (const id of a.charged ?? []) if (!charged.includes(id)) charged.push(id);
  }
  return { fast, charged };
}

/** The Mega-only "+" moves a species has, by printed name. */
export function megaOnlyMoveNamesFor(speciesId: number): string[] {
  return MEGA_ONLY_MOVES.filter((m) => m.speciesId === speciesId).map((m) => m.name);
}
